/**
 * Phase 19 — judging a rule on one item's bag, three-valued (Kleene): a
 * condition whose metric is absent is UNKNOWN; AND is false on any false,
 * else unknown on any unknown; OR is true on any true, else unknown on any
 * unknown; an empty group is unknown. Only a definite TRUE fires — missing
 * data never acts — except EXCLUDE (Phase 22): an item that cannot be judged
 * stays out of the ranking. A score-ranking condition (12/10/2026) reads the
 * item's place in its formula's ranking — rules other than Exclude are judged
 * after the ranking, Exclude rules before it (they decide who is ranked).
 * Hour rules may also test hour tags (a tag context per hour; none = unknown).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FLOW_METRIC_LABELS } from "../../formula/catalog";
import { hourRangeKey, hourRangeLabel } from "../hour-formula";
import { describeRanking, evaluateRanking, type ScoreRank } from "./ranking/score-rank";
import { describeScope, scopeIncomplete } from "./scope";
import { DROP_GEN_EXCLUDE_ACTION, type DropGenRule, type DropGenRuleHit, type DropGenRuleNode, type DropGenRuleOp } from "./rules.types";
import { describeTagNode, evaluateTagNode, usesTags, type DropGenTagContext } from "./tags/tag-node";

export type Tri = boolean | null;
type Bag = Record<string, number>;

const CMP: Record<DropGenRuleOp, (a: number, b: number) => boolean> = {
  "<": (a, b) => a < b,
  "<=": (a, b) => a <= b,
  "=": (a, b) => a === b,
  "!=": (a, b) => a !== b,
  ">": (a, b) => a > b,
  ">=": (a, b) => a >= b,
};

export function evaluateRuleNode(node: DropGenRuleNode, bag: Bag, rank?: ScoreRank, tags?: DropGenTagContext): Tri {
  if (node.kind === "tag") return evaluateTagNode(node, tags);
  if (node.kind === "condition") {
    const v = bag[node.metric];
    return typeof v === "number" && Number.isFinite(v) ? CMP[node.op](v, node.value) : null;
  }
  if (node.kind === "ranking") return evaluateRanking(node, rank);
  if (node.kind === "not") {
    const v = evaluateRuleNode(node.child, bag, rank, tags);
    return v === null ? null : !v;
  }
  if (node.children.length === 0) return null;
  const vals = node.children.map((c) => evaluateRuleNode(c, bag, rank, tags));
  if (node.op === "AND") return vals.includes(false) ? false : vals.includes(null) ? null : true;
  return vals.includes(true) ? true : vals.includes(null) ? null : false;
}

/** The report key a rule reads (the hour formula's range keys). */
export const ruleRangeKey = (r: Pick<DropGenRule, "dateRange" | "dateRangeCustomFrom" | "dateRangeCustomTo">) => hourRangeKey(r);

const fmt = (v: number) => (Number.isInteger(v) ? v.toLocaleString("en-US") : v.toFixed(2));

/** The conditions a rule names, with the item's values, in words. */
function describe(node: DropGenRuleNode, bag: Bag, rank?: ScoreRank, tags?: DropGenTagContext): string[] {
  if (node.kind === "tag") return [describeTagNode(node, tags)];
  if (node.kind === "condition") {
    const v = bag[node.metric];
    const label = FLOW_METRIC_LABELS[node.metric] ?? node.metric;
    return [`${label} ${typeof v === "number" ? fmt(v) : "—"} ${node.op} ${fmt(node.value)}`];
  }
  if (node.kind === "ranking") return [describeRanking(node, rank)];
  if (node.kind === "not") return describe(node.child, bag, rank, tags).map((s) => `not (${s})`);
  return node.children.flatMap((c) => describe(c, bag, rank, tags));
}

/** One rule on one item: fired / unknown (null when it simply does not hold).
 *  An exclude rule that cannot be judged (no data, a value missing) fires:
 *  an item we cannot judge stays out of the ranking — "not judged yet".
 *  A rule that only tests hour tags does not need data in its own window. */
export function judgeRule(rule: DropGenRule, bagOf: (rangeKey: string) => Bag | undefined, rank?: ScoreRank, tags?: DropGenTagContext): DropGenRuleHit | null {
  if (!rule.enabled) return null;
  const key = ruleRangeKey(rule);
  const bag = bagOf(key);
  const range = hourRangeLabel(key);
  const base = { ruleId: rule.id, name: rule.name, action: rule.action };
  const excludes = rule.action === DROP_GEN_EXCLUDE_ACTION;
  const needsBag = readsMetrics(rule.when) || !usesTags(rule.when);
  if (!bag && excludes && needsBag) return { ...base, state: "fired", reason: `no data over ${range} — not judged yet` };
  const verdict = evaluateRuleNode(rule.when, bag ?? {}, rank, tags);
  if (verdict === false) return null;
  if (!bag && verdict === null && needsBag) return { ...base, state: "unknown", reason: `no data over ${range} — the rule does not act` };
  const words = describe(rule.when, bag ?? {}, rank, tags).join(rule.when.kind === "group" && rule.when.op === "OR" ? " or " : " and ");
  // A rule that only reads the ranking (or hour tags) names no window: those come from elsewhere.
  const over = readsMetrics(rule.when) ? ` over ${range}` : "";
  // Phase 26: a scoped Block says which lists it holds (the planner, the chart and the card read this).
  const scoped = rule.scope ? ` · ${describeScope(rule.scope)}` : "";
  if (verdict === true && scopeIncomplete(rule.scope)) return { ...base, state: "unknown", reason: `${words}${over} — the scope names no tag yet, the rule does not act` };
  if (verdict === true) return { ...base, state: "fired", reason: `${words}${over}${scoped}` };
  const why = readsMetrics(rule.when) ? " — a value is missing," : " —";
  return excludes
    ? { ...base, state: "fired", reason: `${words}${over}${why} not judged yet` }
    : { ...base, state: "unknown", reason: `${words}${over}${why} the rule does not act` };
}

const isExclude = (r: DropGenRule) => r.action === DROP_GEN_EXCLUDE_ACTION;

/** Does the tree read a metric (over the rule's range)? A score-ranking or hour-tag-only rule reads no window. */
export function readsMetrics(node: DropGenRuleNode): boolean {
  if (node.kind === "condition") return true;
  if (node.kind === "not") return readsMetrics(node.child);
  return node.kind === "group" && node.children.some(readsMetrics);
}

/** Every rule on one item — exclude rules first (without the ranking: they decide who is ranked); an
 *  excluded item is judged by no other rule; the others read the item's place in the ranking (`rank`).
 *  Hour rules pass the hour's tags (`tags`) to both. */
export function judgeRules(
  rules: DropGenRule[] | undefined,
  bagOf: (rangeKey: string) => Bag | undefined,
  rank?: ScoreRank,
  tags?: DropGenTagContext,
): DropGenRuleHit[] {
  const judge = (list: DropGenRule[], r?: ScoreRank) => list.map((x) => judgeRule(x, bagOf, r, tags)).filter((h): h is DropGenRuleHit => h !== null);
  const out = judge((rules ?? []).filter(isExclude));
  return out.length > 0 ? out : judge((rules ?? []).filter((r) => !isExclude(r)), rank);
}

/** Did a rule with this action fire? */
export const fired = (hits: DropGenRuleHit[] | undefined, action: string) =>
  (hits ?? []).some((h) => h.state === "fired" && h.action === action);

/** Is the item out of the ranking (an exclude rule fired)? */
export const isExcluded = (hits: DropGenRuleHit[] | undefined) => fired(hits, DROP_GEN_EXCLUDE_ACTION);

/** Does an exclude rule take this item out of the ranking? (Rules already first-range-resolved.) */
export const excludedBy = (
  rules: DropGenRule[] | undefined,
  bagOf: (rangeKey: string) => Bag | undefined,
  tags?: DropGenTagContext,
) => (rules ?? []).some((r) => isExclude(r) && judgeRule(r, bagOf, undefined, tags)?.state === "fired");

/** Rules that act beyond excluding — a surface with none needs no rule data of its own. */
export const hasActingRules = (rules: DropGenRule[] | undefined) => (rules ?? []).some((r) => r.enabled && !isExclude(r));
